// frontend/src/pages/SocialStudioPage.js
// Social Studio (docs/social-studio/SPEC.md S6): review each planned week — Print Story, 9+9 grid
// preview, every slide, captions per platform, QA decisions — veto posts, pause all posting, and
// browse the SKU asset index. Backend: /api/v1/studio/* (backend/routes/studioRoutes.js).

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import './SocialStudioPage.css';

const FORMAT_LABELS = {
  F1_PRINT_HERO: 'Print Hero',
  F2_PRINT_CARD: 'Print Card',
  F3_SWATCH_STORY: 'Swatch Story',
  F4_THREE_LOOKS: 'Three Looks',
  F5_FABRIC_GUIDE: 'Fabric Guide',
  F7_STATEMENT: 'Statement',
  F9_TREND_BOARD: 'Trend Board',
  PIN: 'Pin',
};

const STATUS_LABELS = {
  concept: 'Written',
  qa_passed: 'QA passed',
  qa_failed: 'QA failed',
  replaced: 'Replaced by buffer',
  vetoed: 'Vetoed',
  skipped: 'Skipped',
  scheduled: 'Scheduled',
  published: 'Published',
  buffer: 'Ready in buffer',
  used: 'Used',
};

const FORMAT_OPTIONS = Object.keys(FORMAT_LABELS).filter(f => f !== 'PIN');

function formatDay(post) {
  if (!post.date) return '';
  const d = new Date(`${post.date}T00:00:00`);
  return `${d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })} · ${post.timeIst || ''} IST`;
}

function StatusBadge({ status }) {
  return <span className={`ss-badge ss-badge--${status || 'none'}`}>{STATUS_LABELS[status] || status || '—'}</span>;
}

function Gates({ gates }) {
  if (!gates || !gates.length) return null;
  return (
    <ul className="ss-gates">
      {gates.map(g => (
        <li key={g.gate} className={g.passed ? 'ss-gate ss-gate--ok' : 'ss-gate ss-gate--fail'}>
          <span className="ss-gate-mark">{g.passed ? '✓' : '✗'}</span>
          <span className="ss-gate-name">{g.gate.replace(/_/g, ' ')}</span>
          <span className="ss-gate-detail">{g.detail}</span>
        </li>
      ))}
    </ul>
  );
}

function Lightbox({ images, index, onClose, onMove }) {
  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onMove(1);
      if (e.key === 'ArrowLeft') onMove(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onMove]);
  if (!images || index == null) return null;
  return (
    <div className="ss-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <button type="button" className="ss-lightbox-nav ss-lightbox-nav--prev" onClick={e => { e.stopPropagation(); onMove(-1); }} aria-label="Previous slide">‹</button>
      <img src={images[index]} alt={`Slide ${index + 1}`} onClick={e => e.stopPropagation()} />
      <button type="button" className="ss-lightbox-nav ss-lightbox-nav--next" onClick={e => { e.stopPropagation(); onMove(1); }} aria-label="Next slide">›</button>
      <div className="ss-lightbox-count">{index + 1} / {images.length}</div>
    </div>
  );
}

// Thumbnails in lists (renders are full 1080px JPEGs); the lightbox opens the full image.
function SlideStrip({ urls, thumbs, onOpen }) {
  if (!urls || !urls.length) return <div className="ss-noslides">No render yet</div>;
  return (
    <div className="ss-slides">
      {urls.map((u, i) => (
        <button type="button" key={u} className="ss-slide" onClick={() => onOpen(urls, i)} aria-label={`Open slide ${i + 1}`}>
          <img src={(thumbs && thumbs[i]) || u} alt={`Slide ${i + 1}`} loading="lazy" />
        </button>
      ))}
    </div>
  );
}

function Captions({ copy, platform }) {
  const [open, setOpen] = useState(false);
  if (!copy) return null;
  const blocks = platform === 'pinterest'
    ? [['Pin title', copy.pinterest_title], ['Pin description', copy.pinterest_description]]
    : [['Instagram', copy.instagram_caption], ['Facebook', copy.facebook_caption]];
  return (
    <div className="ss-captions">
      <button type="button" className="ss-link" onClick={() => setOpen(o => !o)}>{open ? 'Hide captions' : 'Show captions'}</button>
      {open && (
        <div className="ss-caption-grid">
          {copy.statement && <div className="ss-caption"><h5>Statement</h5><p>{copy.statement}</p></div>}
          {blocks.filter(([, t]) => t).map(([label, text]) => (
            <div className="ss-caption" key={label}><h5>{label}</h5><p>{text}</p></div>
          ))}
          <div className="ss-caption"><h5>Alt text</h5><p>{copy.alt_text}</p></div>
          {copy.hashtags && copy.hashtags.length > 0 && <div className="ss-caption"><h5>Hashtags</h5><p>{copy.hashtags.join(' ')}</p></div>}
        </div>
      )}
    </div>
  );
}

function PostCard({ post, skus, onOpen, onVeto, onUnveto, busy }) {
  const products = (post.productIds || []).map(id => skus[id]).filter(Boolean);
  const canVeto = ['concept', 'qa_passed', 'scheduled'].includes(post.status);
  return (
    <article className={`ss-post ss-post--${post.status}`}>
      <header className="ss-post-head">
        <div>
          <div className="ss-post-day">{formatDay(post)}</div>
          <h4>{FORMAT_LABELS[post.format] || post.format} <span className="ss-muted">· {post.pillar}</span></h4>
          {post.bufferSourceId && <div className="ss-note">From buffer, replacing {post.replaces}</div>}
          {post.movedByComposer && <div className="ss-note">Moved by Grid Composer</div>}
        </div>
        <StatusBadge status={post.status} />
      </header>
      <SlideStrip urls={post.renderUrls} thumbs={post.renderThumbUrls} onOpen={onOpen} />
      {products.length > 0 && (
        <div className="ss-products">
          {products.map(p => (
            <a key={p.productId} href={p.url} target="_blank" rel="noreferrer">{p.sku} · {(post.displayNames || {})[p.productId] || p.title}</a>
          ))}
        </div>
      )}
      <Captions copy={post.copy} platform={post.platform === 'pinterest' ? 'pinterest' : 'meta'} />
      <Gates gates={post.gates} />
      {post.veto && <div className="ss-veto-note">Vetoed by {post.veto.by}: {post.veto.reason}</div>}
      <footer className="ss-post-foot">
        {canVeto && <button type="button" className="ss-btn ss-btn--ghost" disabled={busy} onClick={() => onVeto(post)}>Veto</button>}
        {post.status === 'vetoed' && <button type="button" className="ss-btn ss-btn--ghost" disabled={busy} onClick={() => onUnveto(post)}>Undo veto</button>}
      </footer>
    </article>
  );
}

function GridPreview({ grid, posts }) {
  if (grid && grid.previewUrl) {
    return <img className="ss-grid-img" src={grid.previewUrl} alt="Instagram profile grid preview" />;
  }
  // Fallback: build the grid from first slides (newest first, 3 columns).
  const tiles = [...((grid && grid.previousTiles) || []), ...posts.filter(p => p.channel === 'grid' && p.status === 'qa_passed')]
    .sort((a, b) => String(b.scheduledAt).localeCompare(String(a.scheduledAt)));
  if (!tiles.length) return <div className="ss-empty">No grid tiles yet</div>;
  return (
    <div className="ss-grid-tiles">
      {tiles.map(t => (
        <div key={t.id} className="ss-grid-tile">{t.renderUrls && t.renderUrls[0] ? <img src={(t.renderThumbUrls && t.renderThumbUrls[0]) || t.renderUrls[0]} alt={t.id} /> : <span>{FORMAT_LABELS[t.format]}</span>}</div>
      ))}
    </div>
  );
}

function WeekTab({ week, onOpen, onVeto, onUnveto, busy }) {
  const grid = week.posts.filter(p => p.channel === 'grid' && !(p.status === 'replaced'));
  const replaced = week.posts.filter(p => p.status === 'replaced');
  const s = week.plan.qaSummary;
  return (
    <div className="ss-week">
      <section className="ss-story">
        <span className="ss-swatch" style={{ background: week.plan.story.accent }} aria-hidden="true" />
        <div>
          <h3>{week.plan.story.name}</h3>
          <p>{week.plan.story.theme}</p>
        </div>
        {s && (
          <div className="ss-chips">
            <span className="ss-chip">Grid {s.grid.filled}/{s.grid.slots}{s.grid.fromBuffer ? ` (${s.grid.fromBuffer} from buffer)` : ''}</span>
            <span className="ss-chip">Pins {s.pins.passed}/{s.pins.total}</span>
            <span className={s.gridViolations.length ? 'ss-chip ss-chip--warn' : 'ss-chip ss-chip--ok'}>
              {s.gridViolations.length ? `${s.gridViolations.length} grid issue(s)` : 'Grid rules OK'}
            </span>
          </div>
        )}
      </section>
      <div className="ss-week-body">
        <aside className="ss-grid-panel">
          <h4>Profile grid preview</h4>
          <p className="ss-muted">Newest top-left, cropped as Instagram shows it.</p>
          <GridPreview grid={week.grid} posts={week.posts} />
          {week.grid && week.grid.violations && week.grid.violations.length > 0 && (
            <ul className="ss-violations">{week.grid.violations.map(v => <li key={v}>{v}</li>)}</ul>
          )}
        </aside>
        <div className="ss-posts">
          {grid.map(p => <PostCard key={p.id} post={p} skus={week.skus} onOpen={onOpen} onVeto={onVeto} onUnveto={onUnveto} busy={busy} />)}
          {replaced.length > 0 && (
            <details className="ss-replaced">
              <summary>{replaced.length} post(s) failed QA and were replaced</summary>
              {replaced.map(p => <PostCard key={p.id} post={p} skus={week.skus} onOpen={onOpen} onVeto={onVeto} onUnveto={onUnveto} busy={busy} />)}
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

function PinsTab({ week, onOpen }) {
  const pins = week.posts.filter(p => p.channel === 'pin');
  return (
    <div className="ss-pins">
      {pins.map(p => (
        <article key={p.id} className={`ss-pin ss-post--${p.status}`}>
          <button type="button" className="ss-pin-img" onClick={() => p.renderUrls && p.renderUrls.length && onOpen(p.renderUrls, 0)}>
            {p.renderUrls && p.renderUrls[0] ? <img src={(p.renderThumbUrls && p.renderThumbUrls[0]) || p.renderUrls[0]} alt={p.id} loading="lazy" /> : <span>No render</span>}
          </button>
          <div className="ss-pin-body">
            <div className="ss-post-day">{formatDay(p)}</div>
            <StatusBadge status={p.status} />
            {p.copy && <h5>{p.copy.pinterest_title}</h5>}
            {p.copy && <p>{p.copy.pinterest_description}</p>}
            {p.links && p.links.pinterest && <a className="ss-link" href={p.links.pinterest} target="_blank" rel="noreferrer">Open product link</a>}
            {(p.gates || []).some(g => !g.passed) && <Gates gates={p.gates.filter(g => !g.passed)} />}
          </div>
        </article>
      ))}
    </div>
  );
}

function BufferTab({ jsonFetch, onOpen }) {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    jsonFetch('/api/v1/studio/buffer').then(d => setPosts(d.posts)).catch(e => setError(e.message));
  }, [jsonFetch]);
  if (error) return <div className="ss-error">{error}</div>;
  if (!posts) return <div className="ss-loading">Loading buffer…</div>;
  const ready = posts.filter(p => p.status === 'buffer');
  return (
    <div>
      <p className="ss-muted">{ready.length} evergreen posts ready. A grid slot that fails QA is filled from here after the buffer post passes every check itself.</p>
      <div className="ss-posts ss-posts--cols">
        {posts.map(p => (
          <article key={p.id} className={`ss-post ss-post--${p.status}`}>
            <header className="ss-post-head">
              <h4>{FORMAT_LABELS[p.format]} <span className="ss-muted">· {p.pillar}</span></h4>
              <StatusBadge status={p.status} />
            </header>
            {p.copy && p.copy.statement && <p className="ss-statement">“{p.copy.statement}”</p>}
            {p.displayNames && Object.values(p.displayNames)[0] && <p className="ss-statement">{Object.values(p.displayNames)[0]}</p>}
            <Captions copy={p.copy} platform="meta" />
            {p.usedBy && <div className="ss-note">Used by {p.usedBy}</div>}
          </article>
        ))}
      </div>
    </div>
  );
}

function ControlTab({ control, alerts, onToggleFormat, onAck, busy }) {
  return (
    <div className="ss-control">
      <section className="ss-panel">
        <h4>Paused formats</h4>
        <p className="ss-muted">A format pauses automatically after 2 QA failures in a row; the buffer fills its slots. Untick to resume.</p>
        <div className="ss-format-toggles">
          {FORMAT_OPTIONS.map(f => (
            <label key={f} className="ss-toggle">
              <input type="checkbox" checked={control.pausedFormats.includes(f)} disabled={busy} onChange={() => onToggleFormat(f)} />
              {FORMAT_LABELS[f]}
            </label>
          ))}
        </div>
      </section>
      <section className="ss-panel">
        <h4>Alerts</h4>
        {!alerts.length && <p className="ss-muted">No alerts.</p>}
        <ul className="ss-alerts">
          {alerts.map(a => (
            <li key={a.id} className={`ss-alert ss-alert--${a.level}${a.acknowledged ? ' ss-alert--ack' : ''}`}>
              <div>
                <div className="ss-alert-msg">{a.message}</div>
                <div className="ss-muted">{new Date(a.createdAt).toLocaleString('en-IN')} · {a.kind}</div>
              </div>
              {!a.acknowledged && <button type="button" className="ss-btn ss-btn--ghost" disabled={busy} onClick={() => onAck(a)}>Acknowledge</button>}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function CatalogueTab({ jsonFetch }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const search = async e => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const d = await jsonFetch(`/api/v1/studio/skus?q=${encodeURIComponent(q.trim())}`);
      setResults(d.skus);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div>
      <form className="ss-search" onSubmit={search}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="SKU (e.g. KD-0-TA015703), design code or Shopify product ID" aria-label="Search SKU" />
        <button type="submit" className="ss-btn" disabled={loading || q.trim().length < 2}>{loading ? 'Searching…' : 'Search'}</button>
      </form>
      {error && <div className="ss-error">{error}</div>}
      {results && !results.length && <p className="ss-muted">No match.</p>}
      {(results || []).map(s => (
        <article key={s.productId} className="ss-sku">
          <header>
            <h4><a href={s.url} target="_blank" rel="noreferrer">{s.sku}</a> · {s.title}</h4>
            <div className="ss-muted">{s.fabric || s.fabricType} · {s.widthInch ? `${s.widthInch} inch` : 'width unknown'} · stock {s.stockM}m · {s.promotable ? 'promotable' : 'not promotable'}</div>
            <div className="ss-chips">
              {Object.entries(s.eligibleFormats || {}).map(([f, ok]) => <span key={f} className={ok ? 'ss-chip ss-chip--ok' : 'ss-chip ss-chip--off'}>{FORMAT_LABELS[f] || f}</span>)}
            </div>
            {s.issues && s.issues.length > 0 && <div className="ss-note">Issues: {s.issues.join(', ')}</div>}
          </header>
          <div className="ss-assets">
            {s.assets.filter(a => a.url && /^https:/.test(a.url)).map(a => (
              <figure key={a.url} className={`ss-asset ss-asset--${a.source}${a.usable ? '' : ' ss-asset--off'}`}>
                <img src={`${a.url.split('?')[0]}?width=240`} alt={a.kind} loading="lazy" />
                <figcaption>{a.source} · {a.view || a.outfitCode || a.kind}{a.usable ? '' : ` · excluded (${a.excludedReason})`}</figcaption>
              </figure>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}


// ---- Layouts tab: admin-editable template settings (backend: services/studio/layoutSettings.js) ----

const FONTS_MARKER = '/*__STUDIO_FONTS__*/';
const LAYOUT_FORMATS = ['F1_PRINT_HERO', 'F2_PRINT_CARD', 'F3_SWATCH_STORY', 'F4_THREE_LOOKS', 'F5_FABRIC_GUIDE', 'F7_STATEMENT', 'F9_TREND_BOARD', 'PIN'];
const PREVIEW_SCALE = 0.36;

// Runs the audit embedded in a template document (window.__studioAudit) inside a hidden iframe.
function auditOffscreen(html, canvas, params) {
  return new Promise(resolve => {
    const frame = document.createElement('iframe');
    frame.style.cssText = `position:fixed;left:-20000px;top:0;width:${canvas.width}px;height:${canvas.height}px;border:0;visibility:hidden`;
    frame.onload = async () => {
      try {
        await frame.contentWindow.document.fonts.ready;
        resolve(frame.contentWindow.__studioAudit(params));
      } catch (e) {
        resolve([`audit failed: ${e.message}`]);
      } finally {
        frame.remove();
      }
    };
    frame.srcdoc = html;
    document.body.appendChild(frame);
  });
}

function SettingControl({ field, value, onChange }) {
  if (field.type === 'bool') {
    return <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />;
  }
  if (field.type === 'range') {
    return (
      <span className="ss-range">
        <input type="range" min={field.min} max={field.max} step={field.step} value={value} onChange={e => onChange(Number(e.target.value))} />
        <span className="ss-range-val">{Number(value).toFixed(field.step < 1 ? 1 : 0)}</span>
      </span>
    );
  }
  return (
    <select value={value} onChange={e => onChange(e.target.value)}>
      {field.options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function SettingsSection({ section, schema, values, onChange }) {
  return (
    <fieldset className="ss-settings">
      <legend>{schema.label}</legend>
      {Object.entries(schema.fields).map(([k, f]) => (
        <label key={k} className="ss-setting">
          <span>{f.label}</span>
          <SettingControl field={f} value={values[k]} onChange={v => onChange(section, k, v)} />
        </label>
      ))}
    </fieldset>
  );
}

function PreviewSlide({ html, fontsCss, canvas, params, showZones, onAudit }) {
  const ref = React.useRef(null);
  const srcDoc = useMemo(() => html.replace(FONTS_MARKER, fontsCss || ''), [html, fontsCss]);
  const onLoad = async () => {
    try {
      const w = ref.current.contentWindow;
      await w.document.fonts.ready;
      onAudit(w.__studioAudit(params));
    } catch (e) {
      onAudit([`audit failed: ${e.message}`]);
    }
  };
  const W = canvas.width * PREVIEW_SCALE, H = canvas.height * PREVIEW_SCALE;
  const side = canvas.kind === 'feed' ? (canvas.width - params.gridSafeWidth) / 2 : 0;
  return (
    <div className="ss-preview-slide" style={{ width: W, height: H }}>
      <iframe ref={ref} title="Layout preview" srcDoc={srcDoc} onLoad={onLoad}
        style={{ width: canvas.width, height: canvas.height, transform: `scale(${PREVIEW_SCALE})` }} />
      {showZones && (
        <>
          {side > 0 && <div className="ss-zone ss-zone--crop" style={{ left: 0, width: side * PREVIEW_SCALE }} />}
          {side > 0 && <div className="ss-zone ss-zone--crop" style={{ right: 0, width: side * PREVIEW_SCALE }} />}
          <div className="ss-zone ss-zone--icon" style={{ width: params.topRightZone * PREVIEW_SCALE, height: params.topRightZone * PREVIEW_SCALE }} />
        </>
      )}
    </div>
  );
}

function LayoutsTab({ jsonFetch }) {
  const [meta, setMeta] = useState(null); // { schema, defaults, active, history }
  const [draft, setDraft] = useState(null);
  const [format, setFormat] = useState('F2_PRINT_CARD');
  const [fontsCss, setFontsCss] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [sampleNonce, setSampleNonce] = useState(0);
  const [slideErrors, setSlideErrors] = useState({});
  const [showZones, setShowZones] = useState(true);
  const [check, setCheck] = useState(null); // { running, results: {format: errors[]} }
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const d = await jsonFetch('/api/v1/studio/layout');
    setMeta(d);
    setDraft(JSON.parse(JSON.stringify(d.active.settings)));
  }, [jsonFetch]);

  useEffect(() => {
    load().catch(e => setMessage(e.message));
    jsonFetch('/api/v1/studio/layout/fonts').then(d => setFontsCss(d.css)).catch(() => {});
  }, [load, jsonFetch]);

  // Debounced preview of the selected format with the current draft.
  useEffect(() => {
    if (!draft) return undefined;
    const t = setTimeout(async () => {
      setPreviewError('');
      try {
        const d = await jsonFetch('/api/v1/studio/layout/preview', { method: 'POST', body: JSON.stringify({ format, settings: draft, productId: preview && preview.format === format && sampleNonce === preview.nonce ? preview.sample[0]?.productId : undefined }) });
        setSlideErrors({});
        setPreview({ ...d, nonce: sampleNonce });
      } catch (e) {
        setPreviewError(e.message);
      }
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonFetch, format, draft, sampleNonce]);

  const dirty = meta && draft && JSON.stringify(draft) !== JSON.stringify(meta.active.settings);
  const previewErrs = Object.values(slideErrors).flat();

  const onChange = (section, key, value) => {
    setCheck(null);
    setDraft(d => ({ ...d, [section]: { ...d[section], [key]: value } }));
  };

  const checkAll = async () => {
    setCheck({ running: true, results: {} });
    const results = {};
    for (const f of LAYOUT_FORMATS) {
      try {
        const d = await jsonFetch('/api/v1/studio/layout/preview', { method: 'POST', body: JSON.stringify({ format: f, settings: draft }) });
        const errs = [];
        for (const html of d.slides) errs.push(...await auditOffscreen(html.replace(FONTS_MARKER, fontsCss), d.canvas, d.auditParams));
        results[f] = errs;
      } catch (e) {
        results[f] = [`preview failed: ${e.message}`];
      }
      setCheck({ running: true, results: { ...results } });
    }
    setCheck({ running: false, results });
    return Object.values(results).every(r => r.length === 0);
  };

  const save = async () => {
    setBusy(true);
    setMessage('');
    try {
      const ok = await checkAll();
      if (!ok) { setMessage('Not saved: fix the layout problems listed below first.'); return; }
      const note = window.prompt('Saved layouts apply to weeks checked from now on (already-checked weeks keep their look). Short note for this change:');
      if (note == null) return;
      const d = await jsonFetch('/api/v1/studio/layout', { method: 'POST', body: JSON.stringify({ settings: draft, note }) });
      await load();
      setMessage(`Saved as layout v${d.version}. Applies to weeks checked from now on.`);
    } catch (e) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  const revert = async version => {
    if (!window.confirm(version ? `Make layout v${version} active again (saved as a new version)?` : 'Reset to the built-in defaults (saved as a new version)?')) return;
    setBusy(true);
    try {
      const d = await jsonFetch('/api/v1/studio/layout/revert', { method: 'POST', body: JSON.stringify({ version }) });
      await load();
      setMessage(`Now active: layout v${d.version}.`);
    } catch (e) {
      setMessage(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!meta || !draft) return <div className="ss-loading">{message || 'Loading layouts…'}</div>;

  return (
    <div className="ss-layouts">
      <div className="ss-layouts-head">
        <div>
          <strong>Active: layout v{meta.active.version}{meta.active.version === 0 ? ' (built-in defaults)' : ''}</strong>
          <div className="ss-muted">Changes apply to weeks checked after you save. Brand rules (swatch on AI images, metres note, safe zones, ivory/navy only) are fixed.</div>
        </div>
        <div className="ss-layouts-actions">
          <button type="button" className="ss-btn ss-btn--ghost" disabled={busy || !dirty} onClick={() => { setDraft(JSON.parse(JSON.stringify(meta.active.settings))); setCheck(null); }}>Discard changes</button>
          <button type="button" className="ss-btn ss-btn--ghost" disabled={busy} onClick={() => { setDraft(JSON.parse(JSON.stringify(meta.defaults))); setCheck(null); }}>Load defaults</button>
          <button type="button" className="ss-btn" disabled={busy || !dirty} onClick={save}>{busy ? 'Checking…' : 'Check all formats & save'}</button>
        </div>
      </div>
      {message && <div className="ss-note ss-note--box">{message}</div>}

      <div className="ss-format-pills" role="tablist">
        {LAYOUT_FORMATS.map(f => (
          <button key={f} type="button" className={f === format ? 'ss-pill ss-pill--active' : 'ss-pill'} onClick={() => setFormat(f)}>{FORMAT_LABELS[f]}</button>
        ))}
      </div>

      <div className="ss-layouts-body">
        <div className="ss-layouts-controls">
          <SettingsSection section="global" schema={meta.schema.global} values={draft.global} onChange={onChange} />
          {meta.schema[format] && <SettingsSection section={format} schema={meta.schema[format]} values={draft[format]} onChange={onChange} />}
        </div>
        <div className="ss-layouts-preview">
          <div className="ss-preview-bar">
            <span className="ss-muted">{preview && preview.sample.length ? `Sample: ${preview.sample.map(s => s.sku).join(', ')}` : 'Sample: statement card'}</span>
            <button type="button" className="ss-link" onClick={() => setSampleNonce(n => n + 1)}>Try another product</button>
            <label className="ss-toggle"><input type="checkbox" checked={showZones} onChange={e => setShowZones(e.target.checked)} /> Show safe zones</label>
          </div>
          {previewError && <div className="ss-error">{previewError}</div>}
          {preview && preview.format === format && (
            <div className="ss-preview-slides">
              {preview.slides.map((html, i) => (
                <PreviewSlide key={`${preview.nonce}-${i}-${html.length}`} html={html} fontsCss={fontsCss} canvas={preview.canvas} params={preview.auditParams}
                  showZones={showZones} onAudit={errs => setSlideErrors(e => ({ ...e, [i]: errs }))} />
              ))}
            </div>
          )}
          <div className={previewErrs.length ? 'ss-audit ss-audit--fail' : 'ss-audit ss-audit--ok'}>
            {previewErrs.length ? <ul>{previewErrs.map(e => <li key={e}>{e}</li>)}</ul> : 'Layout check: no problems in this format.'}
          </div>
          {check && (
            <div className="ss-check">
              <h5>{check.running ? 'Checking all formats…' : 'All-formats check'}</h5>
              <ul>
                {LAYOUT_FORMATS.filter(f => check.results[f]).map(f => (
                  <li key={f} className={check.results[f].length ? 'ss-gate--fail' : 'ss-gate--ok'}>
                    {check.results[f].length ? '✗' : '✓'} {FORMAT_LABELS[f]}{check.results[f].length ? `: ${check.results[f].join('; ')}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <section className="ss-panel">
        <h4>Version history</h4>
        {!meta.history.length && <p className="ss-muted">No saved versions yet — the built-in defaults are active.</p>}
        <ul className="ss-history">
          {meta.history.map(h => (
            <li key={h.version}>
              <span><strong>v{h.version}</strong> · {new Date(h.at).toLocaleString('en-IN')} · {h.by}{h.note ? ` · ${h.note}` : ''}</span>
              {h.version !== meta.active.version && <button type="button" className="ss-btn ss-btn--ghost" disabled={busy} onClick={() => revert(h.version)}>Make active</button>}
            </li>
          ))}
        </ul>
        {meta.active.version !== 0 && <button type="button" className="ss-link" disabled={busy} onClick={() => revert(0)}>Reset to built-in defaults</button>}
      </section>
    </div>
  );
}

const TABS = [['week', 'This week'], ['pins', 'Pins'], ['buffer', 'Buffer'], ['control', 'Control & alerts'], ['layouts', 'Layouts'], ['catalogue', 'Catalogue']];

export default function SocialStudioPage() {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState('week');
  const [weeks, setWeeks] = useState([]);
  const [weekId, setWeekId] = useState('');
  const [week, setWeek] = useState(null);
  const [control, setControl] = useState({ pausedAll: false, pausedFormats: [], pausedChannels: [] });
  const [alerts, setAlerts] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState({ images: null, index: null });

  const authedFetch = useCallback(async (url, options = {}) => {
    let token = '';
    if (currentUser && typeof currentUser.getIdToken === 'function') token = await currentUser.getIdToken();
    const base = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    const resolvedUrl = base && url.startsWith('/') ? `${base}${url}` : url;
    return fetch(resolvedUrl, {
      ...options,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'Content-Type': 'application/json', ...(options.headers || {}) },
    });
  }, [currentUser]);

  const jsonFetch = useCallback(async (url, options = {}) => {
    const res = await authedFetch(url, options);
    let data;
    try { data = await res.json(); } catch (_) { throw new Error(`Server returned status ${res.status}`); }
    if (!res.ok || !data?.success) throw new Error(data?.message || data?.error || `Request failed with status ${res.status}`);
    return data;
  }, [authedFetch]);

  const loadControl = useCallback(async () => {
    const d = await jsonFetch('/api/v1/studio/control');
    setControl(d.control);
    setAlerts(d.alerts);
  }, [jsonFetch]);

  useEffect(() => {
    jsonFetch('/api/v1/studio/weeks')
      .then(d => { setWeeks(d.weeks); if (d.weeks.length) setWeekId(w => w || d.weeks[d.weeks.length - 1].weekId); })
      .catch(e => setError(e.message));
    loadControl().catch(e => setError(e.message));
  }, [jsonFetch, loadControl]);

  const loadWeek = useCallback(async id => {
    if (!id) return;
    setWeek(null);
    try {
      const d = await jsonFetch(`/api/v1/studio/weeks/${encodeURIComponent(id)}`);
      setWeek(d);
    } catch (e) {
      setError(e.message);
    }
  }, [jsonFetch]);

  useEffect(() => { loadWeek(weekId); }, [weekId, loadWeek]);

  const sortedWeeks = useMemo(() => weeks.slice().sort((a, b) => a.weekStart.localeCompare(b.weekStart)), [weeks]);

  const act = async fn => {
    setBusy(true);
    setError('');
    try { await fn(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const onVeto = post => {
    const reason = window.prompt(`Veto ${FORMAT_LABELS[post.format] || post.format} on ${post.date}? Give a short reason:`);
    if (reason == null) return;
    act(async () => {
      await jsonFetch(`/api/v1/studio/posts/${encodeURIComponent(post.id)}/veto`, { method: 'POST', body: JSON.stringify({ reason }) });
      await loadWeek(weekId);
      await loadControl();
    });
  };

  const onUnveto = post => act(async () => {
    await jsonFetch(`/api/v1/studio/posts/${encodeURIComponent(post.id)}/unveto`, { method: 'POST' });
    await loadWeek(weekId);
  });

  const onPauseToggle = () => {
    const pausing = !control.pausedAll;
    const reason = window.prompt(pausing ? 'PAUSE ALL posting? Reason:' : 'Resume posting? Reason:');
    if (reason == null) return;
    act(async () => {
      await jsonFetch('/api/v1/studio/control', { method: 'POST', body: JSON.stringify({ pausedAll: pausing, reason }) });
      await loadControl();
    });
  };

  const onToggleFormat = f => act(async () => {
    const next = control.pausedFormats.includes(f) ? control.pausedFormats.filter(x => x !== f) : [...control.pausedFormats, f];
    await jsonFetch('/api/v1/studio/control', { method: 'POST', body: JSON.stringify({ pausedFormats: next, reason: `Toggled ${f}` }) });
    await loadControl();
  });

  const onAck = a => act(async () => {
    await jsonFetch(`/api/v1/studio/alerts/${encodeURIComponent(a.id)}/ack`, { method: 'POST' });
    await loadControl();
  });

  const openLightbox = (images, index) => setLightbox({ images, index });
  const moveLightbox = useCallback(step => setLightbox(l => (l.images ? { ...l, index: (l.index + step + l.images.length) % l.images.length } : l)), []);
  const closeLightbox = useCallback(() => setLightbox({ images: null, index: null }), []);
  const openAlerts = alerts.filter(a => !a.acknowledged).length;

  return (
    <div className="ss-page">
      <header className="ss-header">
        <div>
          <h1>Social Studio</h1>
          <p>Weekly Instagram, Facebook and Pinterest posts — planned, written and checked automatically.</p>
        </div>
        <div className="ss-header-actions">
          <span className={control.pausedAll ? 'ss-state ss-state--paused' : 'ss-state ss-state--live'}>{control.pausedAll ? 'All posting paused' : 'Posting enabled'}</span>
          <button type="button" className={control.pausedAll ? 'ss-btn' : 'ss-btn ss-btn--danger'} disabled={busy} onClick={onPauseToggle}>
            {control.pausedAll ? 'Resume posting' : 'Pause all'}
          </button>
        </div>
      </header>

      {error && <div className="ss-error" role="alert">{error}</div>}

      <nav className="ss-tabs" role="tablist">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} className={tab === id ? 'ss-tab ss-tab--active' : 'ss-tab'} onClick={() => setTab(id)}>
            {label}{id === 'control' && openAlerts > 0 ? ` (${openAlerts})` : ''}
          </button>
        ))}
        {(tab === 'week' || tab === 'pins') && sortedWeeks.length > 0 && (
          <select className="ss-week-select" value={weekId} onChange={e => setWeekId(e.target.value)} aria-label="Week">
            {sortedWeeks.map(w => (
              <option key={w.weekId} value={w.weekId}>Week of {w.weekStart} · {w.story?.name}{w.dryRun ? ' (dry run)' : ''}</option>
            ))}
          </select>
        )}
      </nav>

      {(tab === 'week' || tab === 'pins') && !sortedWeeks.length && !error && <div className="ss-empty">No weeks planned yet.</div>}
      {(tab === 'week' || tab === 'pins') && sortedWeeks.length > 0 && !week && <div className="ss-loading">Loading week…</div>}
      {tab === 'week' && week && <WeekTab week={week} onOpen={openLightbox} onVeto={onVeto} onUnveto={onUnveto} busy={busy} />}
      {tab === 'pins' && week && <PinsTab week={week} onOpen={openLightbox} />}
      {tab === 'buffer' && <BufferTab jsonFetch={jsonFetch} onOpen={openLightbox} />}
      {tab === 'control' && <ControlTab control={control} alerts={alerts} onToggleFormat={onToggleFormat} onAck={onAck} busy={busy} />}
      {tab === 'catalogue' && <CatalogueTab jsonFetch={jsonFetch} />}
      {tab === 'layouts' && <LayoutsTab jsonFetch={jsonFetch} />}

      <Lightbox images={lightbox.images} index={lightbox.index} onClose={closeLightbox} onMove={moveLightbox} />
    </div>
  );
}
